import { updateCloudDocument, uploadCloudDocument } from './cloudApi';
import { suggestCopyName } from './saveTarget';

export const pdfFileName = (name = 'document.pdf') => {
  const leaf = String(name).split(/[\\/]/).at(-1).trim() || 'document';
  return /\.pdf$/i.test(leaf) ? leaf : `${leaf}.pdf`;
};
export const pathFileName = (path) => path?.split(/[\\/]/).at(-1);

// Shared by the editor, tool workspace, and save-before-close/open guards.
// The caller supplies a frozen working File, so edits made during an async
// dialog/upload cannot accidentally be marked as saved.
export async function saveDocumentSnapshot(document, {
  intent = 'save', api = globalThis.window?.electronAPI, saveFile,
  updateCloud = updateCloudDocument, uploadCloud = uploadCloudDocument,
} = {}) {
  if (!document?.file) return null;
  const { file, filePath, cloudDocId } = document;
  const name = pdfFileName(file.name);
  const bytes = new Uint8Array(await file.arrayBuffer());
  const output = file.name === name ? file : new File([bytes], name, { type: 'application/pdf' });
  const exporting = intent === 'export' || intent === 'flatten';
  if (intent === 'cloud' || (intent === 'save' && cloudDocId)) {
    let id = cloudDocId;
    if (id) await updateCloud(id, output, { filename: name });
    else {
      const data = await uploadCloud(output, { filename: name });
      id = data?.document?.id;
      if (!id) throw new Error('Cloud did not confirm the saved document');
    }
    return { ...document, file: output, filePath: null, cloudDocId: id, dirty: false, workingCopy: true };
  }
  const needsCopy = document.protectOriginal && !document.workingCopy;
  const suggested = intent === 'flatten' ? name.replace(/\.pdf$/i, ' (flattened).pdf')
    : needsCopy && intent === 'save' ? suggestCopyName(name) : name;
  let path;
  if (api && intent === 'save' && filePath && !needsCopy && pathFileName(filePath) === name) {
    await api.writeFile(filePath, bytes);
    path = filePath;
  } else if (saveFile) {
    path = await saveFile(bytes, suggested);
  } else if (api) {
    path = await api.saveFile(bytes, suggested);
  } else {
    const url = URL.createObjectURL(output);
    const link = window.document.createElement('a');
    link.href = url; link.download = suggested; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    path = suggested;
  }
  if (!path) return null; // Cancellation is not success.
  if (exporting) return document; // A copy does not change the active destination or saved baseline.
  const savedName = pdfFileName(pathFileName(path) || suggested);
  return { ...document, file: new File([bytes], savedName, { type: 'application/pdf' }),
    filePath: api ? path : null, cloudDocId: null, dirty: false, workingCopy: true };
}
